import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Calculator,
  Radio,
  Check,
  X,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Calculates intermodulation products for a set of frequencies
 * IMD products that can cause interference:
 * - 2nd order: f1 ± f2
 * - 3rd order: 2*f1 - f2, 2*f2 - f1 (most problematic)
 * - 5th order: 3*f1 - 2*f2, 3*f2 - 2*f1
 */
export function calculateIntermodProducts(frequencies, options = {}) {
  const {
    minFreq = 480,
    maxFreq = 980,
    safetyMargin = 0.025, // 25 kHz safety margin
    includeSecondOrder = false,
    includeFifthOrder = false,
  } = options;

  const products = [];
  const conflicts = [];

  // Sort frequencies
  const sortedFreqs = [...frequencies].sort((a, b) => a - b);

  // Calculate 3rd order products (most important)
  for (let i = 0; i < sortedFreqs.length; i++) {
    for (let j = i + 1; j < sortedFreqs.length; j++) {
      const f1 = sortedFreqs[i];
      const f2 = sortedFreqs[j];

      // 3rd order: 2*f1 - f2 and 2*f2 - f1
      const imd3a = 2 * f1 - f2;
      const imd3b = 2 * f2 - f1;

      if (imd3a >= minFreq && imd3a <= maxFreq) {
        products.push({
          type: "3rd",
          formula: `2×${f1.toFixed(3)} - ${f2.toFixed(3)}`,
          frequency: imd3a,
          sources: [f1, f2],
          severity: "high",
        });
      }

      if (imd3b >= minFreq && imd3b <= maxFreq) {
        products.push({
          type: "3rd",
          formula: `2×${f2.toFixed(3)} - ${f1.toFixed(3)}`,
          frequency: imd3b,
          sources: [f1, f2],
          severity: "high",
        });
      }

      // 5th order (optional)
      if (includeFifthOrder) {
        const imd5a = 3 * f1 - 2 * f2;
        const imd5b = 3 * f2 - 2 * f1;

        if (imd5a >= minFreq && imd5a <= maxFreq) {
          products.push({
            type: "5th",
            formula: `3×${f1.toFixed(3)} - 2×${f2.toFixed(3)}`,
            frequency: imd5a,
            sources: [f1, f2],
            severity: "medium",
          });
        }

        if (imd5b >= minFreq && imd5b <= maxFreq) {
          products.push({
            type: "5th",
            formula: `3×${f2.toFixed(3)} - 2×${f1.toFixed(3)}`,
            frequency: imd5b,
            sources: [f1, f2],
            severity: "medium",
          });
        }
      }

      // 2nd order (optional, less common in professional gear)
      if (includeSecondOrder) {
        const sum = f1 + f2;
        const diff = Math.abs(f1 - f2);

        if (sum >= minFreq && sum <= maxFreq) {
          products.push({
            type: "2nd",
            formula: `${f1.toFixed(3)} + ${f2.toFixed(3)}`,
            frequency: sum,
            sources: [f1, f2],
            severity: "low",
          });
        }

        if (diff >= minFreq && diff <= maxFreq) {
          products.push({
            type: "2nd",
            formula: `|${f1.toFixed(3)} - ${f2.toFixed(3)}|`,
            frequency: diff,
            sources: [f1, f2],
            severity: "low",
          });
        }
      }
    }
  }

  // Check for conflicts with assigned frequencies
  for (const product of products) {
    for (const freq of sortedFreqs) {
      const distance = Math.abs(product.frequency - freq);
      if (distance <= safetyMargin && distance > 0.001) {
        conflicts.push({
          ...product,
          conflictsWith: freq,
          distance: distance * 1000, // Convert to kHz
        });
      }
    }
  }

  return {
    products,
    conflicts,
    totalProducts: products.length,
    hasConflicts: conflicts.length > 0,
    safetyStatus: conflicts.length === 0 ? "safe" : conflicts.length <= 2 ? "warning" : "danger",
  };
}

/**
 * IntermodCalculator Component
 * Shows intermodulation analysis for a set of frequencies
 */
export function IntermodCalculator({ frequencies = [], systems = [], minFreq = 480, maxFreq = 980 }) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [includeSecondOrder, setIncludeSecondOrder] = useState(false);
  const [includeFifthOrder, setIncludeFifthOrder] = useState(true);

  // Extract frequencies from systems if not provided directly
  const activeFrequencies = useMemo(() => {
    if (frequencies.length > 0) return frequencies;
    return systems
      .filter((s) => s.frequency && s.frequency > 0)
      .map((s) => parseFloat(s.frequency));
  }, [frequencies, systems]);

  // Calculate intermod products
  const analysis = useMemo(() => {
    if (activeFrequencies.length < 2) {
      return { products: [], conflicts: [], totalProducts: 0, hasConflicts: false, safetyStatus: "safe" };
    }
    return calculateIntermodProducts(activeFrequencies, {
      minFreq,
      maxFreq,
      includeSecondOrder,
      includeFifthOrder,
    });
  }, [activeFrequencies, minFreq, maxFreq, includeSecondOrder, includeFifthOrder]);

  if (activeFrequencies.length < 2) {
    return (
      <div className="p-4 bg-[#111111] border border-white/[0.08] rounded-sm">
        <div className="flex items-center gap-2 text-[#71717A]">
          <Calculator className="w-4 h-4" />
          <span className="text-sm">Se necesitan al menos 2 frecuencias para calcular intermodulación</span>
        </div>
      </div>
    );
  }

  const statusColors = {
    safe: "bg-[#34C759]/10 border-[#34C759]/30 text-[#34C759]",
    warning: "bg-[#FFCC00]/10 border-[#FFCC00]/30 text-[#FFCC00]",
    danger: "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]",
  };

  const statusIcons = {
    safe: <Check className="w-5 h-5" />,
    warning: <AlertTriangle className="w-5 h-5" />,
    danger: <X className="w-5 h-5" />,
  };

  const statusMessages = {
    safe: "Sin conflictos de intermodulación detectados",
    warning: `${analysis.conflicts.length} conflicto(s) potencial(es) detectado(s)`,
    danger: `¡${analysis.conflicts.length} conflictos críticos de intermodulación!`,
  };

  return (
    <div className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/[0.08]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#34C759]" />
            <h3 className="font-heading font-semibold text-[#F4F4F5]">
              Análisis de Intermodulación
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-[#71717A]" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-[#1A1A1A] border-white/10">
                  <p className="text-xs">
                    La intermodulación ocurre cuando dos señales RF se mezclan y crean 
                    frecuencias espurias que pueden interferir con otros canales.
                    Los productos de 3er orden (2f₁-f₂) son los más problemáticos.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#71717A]">
              {activeFrequencies.length} frecuencias activas
            </span>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`p-4 border-b border-white/[0.08] ${statusColors[analysis.safetyStatus]}`}>
        <div className="flex items-center gap-3">
          {statusIcons[analysis.safetyStatus]}
          <div>
            <p className="font-medium">{statusMessages[analysis.safetyStatus]}</p>
            <p className="text-xs opacity-80 mt-0.5">
              {analysis.totalProducts} productos de intermod calculados en el rango {minFreq}-{maxFreq} MHz
            </p>
          </div>
        </div>
      </div>

      {/* Conflicts List */}
      {analysis.conflicts.length > 0 && (
        <div className="p-4 border-b border-white/[0.08]">
          <h4 className="text-sm font-medium text-[#FF3B30] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Conflictos Detectados
          </h4>
          <div className="space-y-2">
            {analysis.conflicts.map((conflict, idx) => (
              <div
                key={idx}
                className="p-3 bg-[#FF3B30]/5 border border-[#FF3B30]/20 rounded-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#F4F4F5]">
                      <span className="font-mono text-[#FF3B30]">
                        {conflict.frequency.toFixed(3)} MHz
                      </span>
                      <span className="text-[#71717A] mx-2">interfiere con</span>
                      <span className="font-mono text-[#FFCC00]">
                        {conflict.conflictsWith.toFixed(3)} MHz
                      </span>
                    </p>
                    <p className="text-xs text-[#71717A] mt-1">
                      {conflict.formula} = {conflict.frequency.toFixed(3)} MHz
                      <span className="ml-2">(distancia: {conflict.distance.toFixed(1)} kHz)</span>
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    conflict.type === "3rd" ? "bg-[#FF3B30]/20 text-[#FF3B30]" :
                    conflict.type === "5th" ? "bg-[#FFCC00]/20 text-[#FFCC00]" :
                    "bg-[#71717A]/20 text-[#71717A]"
                  }`}>
                    {conflict.type} orden
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toggle Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full p-3 flex items-center justify-center gap-2 text-sm text-[#71717A] hover:text-[#F4F4F5] hover:bg-white/[0.02] transition-colors"
      >
        {showDetails ? (
          <>
            <ChevronUp className="w-4 h-4" />
            Ocultar detalles
          </>
        ) : (
          <>
            <ChevronDown className="w-4 h-4" />
            Ver todos los productos de intermod ({analysis.totalProducts})
          </>
        )}
      </button>

      {/* Detailed Products List */}
      {showDetails && (
        <div className="border-t border-white/[0.08]">
          {/* Options */}
          <div className="p-3 bg-[#0A0A0A] border-b border-white/[0.08] flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-[#71717A] cursor-pointer">
              <input
                type="checkbox"
                checked={includeFifthOrder}
                onChange={(e) => setIncludeFifthOrder(e.target.checked)}
                className="rounded border-white/20 bg-transparent"
              />
              5º Orden
            </label>
            <label className="flex items-center gap-2 text-xs text-[#71717A] cursor-pointer">
              <input
                type="checkbox"
                checked={includeSecondOrder}
                onChange={(e) => setIncludeSecondOrder(e.target.checked)}
                className="rounded border-white/20 bg-transparent"
              />
              2º Orden
            </label>
          </div>

          {/* Products Table */}
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-[#71717A] font-medium">Orden</th>
                  <th className="px-4 py-2 text-left text-[#71717A] font-medium">Fórmula</th>
                  <th className="px-4 py-2 text-right text-[#71717A] font-medium">Frecuencia</th>
                  <th className="px-4 py-2 text-center text-[#71717A] font-medium">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.05]">
                {analysis.products.map((product, idx) => {
                  const isConflict = analysis.conflicts.some(
                    (c) => Math.abs(c.frequency - product.frequency) < 0.001
                  );
                  return (
                    <tr
                      key={idx}
                      className={isConflict ? "bg-[#FF3B30]/5" : "hover:bg-white/[0.02]"}
                    >
                      <td className="px-4 py-2">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${
                          product.type === "3rd" ? "bg-[#FF3B30]/20 text-[#FF3B30]" :
                          product.type === "5th" ? "bg-[#FFCC00]/20 text-[#FFCC00]" :
                          "bg-[#71717A]/20 text-[#71717A]"
                        }`}>
                          {product.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-[#A1A1AA]">
                        {product.formula}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-[#34C759]">
                        {product.frequency.toFixed(3)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {isConflict ? (
                          <X className="w-4 h-4 text-[#FF3B30] inline" />
                        ) : (
                          <Check className="w-4 h-4 text-[#34C759] inline" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Active Frequencies Reference */}
          <div className="p-3 bg-[#0A0A0A] border-t border-white/[0.08]">
            <p className="text-xs text-[#71717A] mb-2">Frecuencias activas:</p>
            <div className="flex flex-wrap gap-2">
              {activeFrequencies.map((freq, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-[#34C759]/10 text-[#34C759] text-xs font-mono rounded"
                >
                  {freq.toFixed(3)} MHz
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IntermodCalculator;
